import {
	invalidateIntegrationQuery,
	useAdminAccountQuery,
	useGetExternalIntegrationQuery,
	usePostExternalIntegrationResetIdleMutation,
	usePostExternalIntegrationSuspendMutation,
} from '@Queries/index';
import * as Api from '@ViewModels';
import { EtlState } from '@ViewModels';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbSeparator,
} from '@WebComponents/Breadcrumb';
import { Button } from '@WebComponents/Button';
import { Portal } from '@WebComponents/Portal';
import { RefreshIcon } from '@WebComponents/svgs/icons/RefreshIcon';
import { css } from 'aphrodite';
import { Tabs } from 'radix-ui';
import * as React from 'react';
import { NavLink, useHistory, useRouteMatch } from 'react-router-dom';
import { MastheadPortalId } from '../../../../../models/AdminModels';
import { useAdminUserSession } from '../../../../../models/hooks/adminStateHooks';
import { AdminPermission } from '../../../../../viewmodels/AdminViewModels';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../../web/components/ConfirmationDialog';
import { ConnectionsIcon } from '../../../../../web/components/svgs/icons/ConnectionsIcon';
import { HistoryIcon } from '../../../../../web/components/svgs/icons/HistoryIcon';
import { SettingsIcon } from '../../../../../web/components/svgs/icons/SettingsIcon';
import { WarningIcon } from '../../../../../web/components/svgs/icons/WarningIcon';
import { brandPrimary, navigation } from '../../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../../web/styles/styles';
import { ConnectionsView } from './components/Connections';
import { ETLStages } from './components/ETLStages';
import { ETLVisuals } from './components/ETLVisuals';
import { RecentActivityView } from './components/RecentActivity';
import { SettingsView } from './components/SettingsView';
import { usePollingRefresh } from './hooks/usePollingRefresh';
import { styleSheet } from './styles';

enum TabValues {
	Settings = 'settings',
	Connections = 'connections',
	RecentActivity = 'recentActivity',
}

export const ETLView = () => {
	const match = useRouteMatch<{ id: string; integrationProvider: string; tab?: string }>();
	const { id: accountId, integrationProvider, tab } = match.params;
	const history = useHistory();
	const [selectedTab, setSelectedTab] = React.useState<TabValues>((tab as TabValues) || TabValues.Settings);
	const userSession = useAdminUserSession();
	const [isRefreshing, setIsRefreshing] = React.useState(false);
	const accountQuery = useAdminAccountQuery({ accountId });
	const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
	const [showConfirmationDialog, setShowConfirmationDialog] = React.useState(false);
	const [pendingTabChange, setPendingTabChange] = React.useState<TabValues | null>(null);
	const [editingSettings, setEditingSettings] = React.useState(false);

	const onSelectedTabChanged = (value: string) => {
		const newTab = value as TabValues;

		if (hasUnsavedChanges && selectedTab !== newTab) {
			setPendingTabChange(newTab);
			setShowConfirmationDialog(true);
			return;
		}

		setSelectedTab(newTab);
		history.replace({
			pathname: `/accounts/${accountId}/integrations/${integrationProvider}/${value}`,
		});
	};

	React.useEffect(() => {
		// This will trigger for a refresh or closing the tab
		// We can't reset the pipeline to idle if the user is editing settings
		// due to restrictions on the browser's confirmation dialog
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (hasUnsavedChanges) {
				event.preventDefault();
				return '';
			}
		};
		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [hasUnsavedChanges, accountId, integrationProvider]);

	const impersonationContext = React.useMemo(() => {
		return { account: { id: accountId } } as Api.IImpersonationContext;
	}, [accountId]);

	const postExternalIntegrationResetIdleMutation = usePostExternalIntegrationResetIdleMutation({
		impersonationContext,
		onSuccess: () => refreshIntegration(),
	});

	const onConfirmationNavigationClose = React.useCallback(
		async (result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
			setShowConfirmationDialog(false);
			setPendingTabChange(null);
			if (result?.isDestructive && !cancel) {
				setSelectedTab(pendingTabChange);
				// If they are navigating away while editing settings, do them a favor
				// and reset to idle so the pipeline doesn't sit in a suspended state
				if (editingSettings) {
					await postExternalIntegrationResetIdleMutation.mutateAsync({
						integrationProvider: integrationProvider as Api.IntegrationProvider,
					});
				}
				history.replace({
					pathname: `/accounts/${accountId}/integrations/${integrationProvider}/${pendingTabChange}`,
				});
				setHasUnsavedChanges(false);
			}
		},
		[
			pendingTabChange,
			editingSettings,
			history,
			accountId,
			integrationProvider,
			postExternalIntegrationResetIdleMutation,
		]
	);

	const integrationQuery = useGetExternalIntegrationQuery({
		impersonationContext,
		provider: integrationProvider as Api.IntegrationProvider,
	});

	// Stop the refresh spinner when fetching completes
	// Uses isFetching instead of isSuccess because isSuccess doesn't toggle during refetch
	React.useEffect(() => {
		if (isRefreshing && !integrationQuery.isFetching && integrationQuery.isSuccess) {
			// Small delay to ensure the animation is visible
			const timer = setTimeout(() => {
				setIsRefreshing(false);
			}, 500);
			return () => clearTimeout(timer);
		}
	}, [isRefreshing, integrationQuery.isFetching, integrationQuery.isSuccess]);

	const refreshIntegration = () => {
		setIsRefreshing(true);
		invalidateIntegrationQuery({
			impersonationContext,
			provider: integrationProvider as Api.IntegrationProvider,
		});
	};

	const postExternalIntegrationSuspendMutation = usePostExternalIntegrationSuspendMutation({
		impersonationContext,
		onSuccess: () => refreshIntegration(),
	});

	const stopSync = () => {
		postExternalIntegrationSuspendMutation.mutateAsync({
			integrationProvider: integrationProvider as Api.IntegrationProvider,
		});
	};

	const resetToIdle = () => {
		postExternalIntegrationResetIdleMutation.mutateAsync({
			integrationProvider: integrationProvider as Api.IntegrationProvider,
		});
	};

	usePollingRefresh(refreshIntegration);

	if (!integrationQuery.data) return null;

	const title = `Manage ${integrationQuery.data.provider}`;

	const anyStagesInState = (etlState: EtlState) => {
		return [
			integrationQuery.data.etl.preProcess,
			integrationQuery.data.etl.extract,
			integrationQuery.data.etl.transform,
			integrationQuery.data.etl.load,
		]
			.filter(Boolean)
			.some(stage => stage.state === etlState);
	};

	const anyStagesAreInSuspendedWithErrorState = anyStagesInState(EtlState.SuspendedWithError);
	const anyStagesAreInSuspendedState = anyStagesInState(EtlState.Suspended);
	const anyStagesAreInProgress = anyStagesInState(EtlState.InProgress);

	const isInSuspendedState = anyStagesAreInSuspendedWithErrorState || anyStagesAreInSuspendedState;
	const isResetToIdleDisabled = () => {
		return (
			!isInSuspendedState ||
			(anyStagesAreInSuspendedWithErrorState && !userSession.hasPermission(AdminPermission.OnlyForDevelopers))
		);
	};

	const getResetToIdleDisabledText = () => {
		if (anyStagesAreInSuspendedWithErrorState && !userSession.hasPermission(AdminPermission.OnlyForDevelopers)) {
			return 'Contact Engineering to reset to idle.';
		} else if (!isInSuspendedState) {
			return 'Etl State must be suspended to reset to idle.';
		}

		return undefined;
	};

	return (
		<>
			<div className={css(styleSheet.container)}>
				<Portal destination={MastheadPortalId}>
					<Breadcrumb>
						<BreadcrumbList>
							<BreadcrumbItem>
								<BreadcrumbLink asChild>
									<NavLink to='/accounts'>Accounts</NavLink>
								</BreadcrumbLink>
							</BreadcrumbItem>
							<BreadcrumbSeparator />
							<BreadcrumbItem>
								<BreadcrumbLink asChild>
									<NavLink to={`/accounts/${accountId}`}>{accountQuery.data?.companyName}</NavLink>
								</BreadcrumbLink>
							</BreadcrumbItem>
							<BreadcrumbSeparator />
							<BreadcrumbItem>
								<BreadcrumbLink asChild>
									<NavLink to={`/accounts/${accountId}?tab=Integrations`}>Integrations</NavLink>
								</BreadcrumbLink>
							</BreadcrumbItem>
							<BreadcrumbSeparator />
							<BreadcrumbItem>
								<BreadcrumbLink asChild>
									<NavLink to={`/accounts/${accountId}/integrations/${integrationProvider}`}>{title}</NavLink>
								</BreadcrumbLink>
							</BreadcrumbItem>
						</BreadcrumbList>
					</Breadcrumb>
				</Portal>
				<header className={css(styleSheet.header)}>
					<Button kind='destructive' size='small' onClick={() => stopSync()} disabled={isInSuspendedState}>
						Suspend
					</Button>
					<Button
						kind='destructive'
						size='small'
						onClick={() => resetToIdle()}
						disabled={isResetToIdleDisabled()}
						title={getResetToIdleDisabledText()}
					>
						Reset to Idle
					</Button>

					<Button kind='reverse' size='small' onClick={() => refreshIntegration()}>
						<RefreshIcon height={12} width={12} className={css(isRefreshing && baseStyleSheet.spinningIcon)} />
					</Button>
				</header>
				<div className={css(styleSheet.etlStagesContainer)}>
					<ETLVisuals etl={integrationQuery.data.etl} />
					<ETLStages
						etl={integrationQuery.data.etl}
						provider={integrationProvider as Api.IntegrationProvider}
						impersonationContext={impersonationContext}
						anyStageInProgress={anyStagesAreInProgress}
						isPipelineSuspended={isInSuspendedState}
					/>
				</div>
				<Tabs.Root className='tab-view' value={selectedTab} onValueChange={onSelectedTabChanged}>
					<Tabs.List className={`tab-view-tabbar-tabs ${css(styleSheet.tabViewTabs)}`}>
						<Tabs.Trigger
							value={TabValues.Settings}
							className={`tab-view-tabbar-item ${
								selectedTab === TabValues.Settings ? `tabbar-item-selected` : ''
							} ${css(styleSheet.tabViewItem)}`}
						>
							<span>
								Settings
								<SettingsIcon
									className={css(styleSheet.icons)}
									stroke={selectedTab === TabValues.Settings ? brandPrimary : navigation}
								/>
							</span>
						</Tabs.Trigger>

						{integrationProvider !== Api.IntegrationProvider.ServiceTitan && (
							<Tabs.Trigger
								value={TabValues.Connections}
								className={`tab-view-tabbar-item ${
									selectedTab === TabValues.Connections ? `tabbar-item-selected` : ''
								} ${css(styleSheet.tabViewItem)}`}
							>
								<span>
									Connections
									<ConnectionsIcon
										className={css(styleSheet.icons)}
										fillColor={selectedTab === TabValues.Connections ? brandPrimary : navigation}
									/>
								</span>
							</Tabs.Trigger>
						)}

						<Tabs.Trigger
							value={TabValues.RecentActivity}
							className={`tab-view-tabbar-item ${
								selectedTab === TabValues.RecentActivity ? `tabbar-item-selected` : ''
							} ${css(styleSheet.tabViewItem)}`}
						>
							<span>
								Recent Activity
								<HistoryIcon
									className={css(styleSheet.icons)}
									fillColor={selectedTab === TabValues.RecentActivity ? brandPrimary : navigation}
								/>
							</span>
						</Tabs.Trigger>
					</Tabs.List>

					<Tabs.Content value={TabValues.Settings} className='tab-view-content'>
						<div className={css(styleSheet.section)}>
							<SettingsView
								impersonationContext={impersonationContext}
								integration={integrationQuery.data}
								provider={integrationProvider as Api.IntegrationProvider}
								setHasUnsavedChanges={setHasUnsavedChanges}
								editingSettings={editingSettings}
								setEditingSettings={setEditingSettings}
							/>
						</div>
					</Tabs.Content>

					{integrationProvider !== Api.IntegrationProvider.ServiceTitan && (
						<Tabs.Content value={TabValues.Connections} className='tab-view-content'>
							<div className={css(styleSheet.section)}>
								<ConnectionsView
									integrationProvider={integrationProvider as Api.IntegrationProvider}
									impersonationContext={impersonationContext}
								/>
							</div>
						</Tabs.Content>
					)}

					<Tabs.Content value={TabValues.RecentActivity} className='tab-view-content'>
						<div className={css(styleSheet.section)}>
							<RecentActivityView
								impersonationContext={impersonationContext}
								integrationProvider={integrationProvider as Api.IntegrationProvider}
							/>
						</div>
					</Tabs.Content>
				</Tabs.Root>
			</div>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: showConfirmationDialog,
					onRequestClose: onConfirmationNavigationClose,
				}}
				options={[
					{
						isDestructive: true,
						representedObject: true,
						title: 'Confirm',
					},
					{
						isCancel: true,
						representedObject: false,
						title: 'Cancel',
					},
				]}
				title='Unsaved Changes'
			>
				<div className={css(styleSheet.confirmationDialogDescription)}>
					You have unsaved changes. Are you sure you want to navigate away? Any unsaved changes will be lost.
				</div>
			</ConfirmationDialog>
		</>
	);
};
